import {
    App,
    AppEntity,
    AppNotificationType,
    AppPlatform,
    AppStoreMinimalApp,
    BuildDistroNotificationType,
} from '../types';
import { BaseAuthedApiRequest } from '../api';
import { CreateEditReleaseType } from './release';

export const TRY_CREATE_APP = 'TRY_CREATE_APP';
export const TRY_ADD_APPS = 'TRY_ADD_APPS';
export const APPS_ADDED_SUCCESS = 'APPS_ADDED_SUCCESS';
export const FETCH_APP_STATE_SUCCESS = 'FETCH_APP_STATE_SUCCESS';
export const APP_ENTITY_UPDATED = 'APP_ENTITY_UPDATED';
export const APPS_UPDATED = 'APPS_UPDATED';
export const APP_STATE_UPDATED = 'APP_STATE_UPDATED';
export const PREDICT_NEXT_RELEASE_VERSION = 'PREDICT_NEXT_RELEASE_VERSION';
export const TRY_DELETE_APP_ENTITY = 'TRY_DELETE_APP_ENTITY';
export const APPS_DELETED = 'APPS_DELETED';
export const TRY_SET_AUTOMATION_PAUSE = 'TRY_SET_AUTOMATION_PAUSE';
export const SET_AUTOMATION_PAUSE_SUCCESS = 'SET_AUTOMATION_PAUSE_SUCCESS';

export interface SendBinaryInstallLinkToCommsRequest extends BaseAuthedApiRequest {
    appId: string;
    artifactId: string;
    workflowRunId: string;
    version: string;
}

export interface SendInstallLinkToMyselfRequest extends BaseAuthedApiRequest {
    appId: string;
    buildId: string;
    bucketId: string;
}

export interface SendNotificationTestDMRequest extends BaseAuthedApiRequest {
    appId: string;
    notificationId: AppNotificationType | BuildDistroNotificationType;
}

export interface GenerateBinaryInstallLinkRequest extends BaseAuthedApiRequest {
    appId: string;
    artifactId: string;
    workflowRunId: string;
    version: string;
}

export interface CreateAppRequest extends BaseAuthedApiRequest {
    orgIdParam: string;
    name: string;
    platform: AppPlatform;
    appId?: string;
    includeOTAPlatform: boolean;
    countryCode?: string;
}

export interface AddAppData {
    name: string;
    platform: AppPlatform;
    appStoreId: string;
    id?: number;
    imageUrl: string;
    includeOTAPlatform: boolean;
    countryCode: string;
}

export interface AddAppsRequest extends BaseAuthedApiRequest {
    orgIdParam: string;
    appsToAdd: AddAppData[];
}

export interface CreateAppAction {
    type: typeof TRY_CREATE_APP;
    payload: CreateAppRequest;
}

export interface AddAppsAction {
    type: typeof TRY_ADD_APPS;
    payload: AddAppsRequest;
}

export interface AppStateResponse {
    app: App;
    orgId: string;
    orgIdParam: string;
    appId: string;
    appIdParam: string;
}

export interface FetchAppStateSuccessAction {
    type: typeof FETCH_APP_STATE_SUCCESS;
    payload: AppStateResponse;
}

export interface AppSearchRequest {
    countryCode: string;
    queryString: string;
}

export type AppSearchResponse = AppStoreMinimalApp[][];

export interface ConnectedAppEntityResponse {
    orgId: string;
    orgIdParam: string;
    appGroupId: string;
    app: AppEntity;
}

export interface ValidateReleaseVersionRequest extends BaseAuthedApiRequest {
    appId: string;
    version: string;
    releaseType: CreateEditReleaseType;
    releaseId?: string;
    sourceVersion?: string;
}

export interface PredictReleaseVersionRequest extends BaseAuthedApiRequest {
    appId: string;
    isHotfix: boolean;
}

export interface GetPossibleBaseTagsRequest extends BaseAuthedApiRequest {
    appId: string;
}

export interface GetPossibleSourceVersionsForRollbackRequest extends BaseAuthedApiRequest {
    appId: string;
}

export interface DeleteAppRequest extends BaseAuthedApiRequest {
    appId: string;
    appIdParam: string;
    orgIdParam: string;
    appGroupId: string;
}

export interface DeletedAppEntityPayload extends BaseAuthedApiRequest {
    appId: string;
    orgIdParam: string;
    appGroupId: string;
}

export interface SetAutomationPauseRequest extends BaseAuthedApiRequest {
    appId: string;
    enabled: boolean;
    automationIds?: string[];
}

export interface SetAutomationPauseSuccessPayload {
    appId: string;
    allAutomationsPaused: boolean;
    pausedAutomationTypes: string[];
}

// Actions

export interface AppsAddedSuccessAction {
    type: typeof APPS_ADDED_SUCCESS;
    payload: ConnectedAppEntityResponse[];
}

export interface AppsUpdatedAction {
    type: typeof APPS_UPDATED;
    payload: ConnectedAppEntityResponse[];
}

export interface AppEntityUpdatedAction {
    type: typeof APP_ENTITY_UPDATED;
    payload: AppEntity;
}

export interface AppStateUpdatedAction {
    type: typeof APP_STATE_UPDATED;
    payload: ConnectedAppEntityResponse;
}

export interface TryDeleteAppAction {
    type: typeof TRY_DELETE_APP_ENTITY;
    payload: DeleteAppRequest;
}

export interface TrySetAutomationPauseAction {
    type: typeof TRY_SET_AUTOMATION_PAUSE;
    payload: {
        requestData: SetAutomationPauseRequest;
        id: string | null;
    };
}

export interface SetAutomationPauseSuccessAction {
    type: typeof SET_AUTOMATION_PAUSE_SUCCESS;
    payload: SetAutomationPauseSuccessPayload;
}

export interface AppsDeletedAction {
    type: typeof APPS_DELETED;
    payload: DeletedAppEntityPayload[];
}

// Action Creators

export const appsAddedSuccess: (payload: ConnectedAppEntityResponse[]) => AppsAddedSuccessAction = (payload) => {
    return {
        type: APPS_ADDED_SUCCESS,
        payload: payload,
    };
};

export const fetchAppStateSuccess: (payload: AppStateResponse) => FetchAppStateSuccessAction = (payload) => {
    return {
        type: FETCH_APP_STATE_SUCCESS,
        payload: payload,
    };
};

export const appEntityUpdated: (payload: AppEntity) => AppEntityUpdatedAction = (payload) => {
    return {
        type: APP_ENTITY_UPDATED,
        payload: payload,
    };
};

export const appsUpdated: (payload: ConnectedAppEntityResponse[]) => AppsUpdatedAction = (payload) => {
    return {
        type: APPS_UPDATED,
        payload,
    };
};

export const appStateUpdated: (payload: ConnectedAppEntityResponse) => AppStateUpdatedAction = (payload) => {
    return {
        type: APP_STATE_UPDATED,
        payload: payload,
    };
};

export const tryCreateApp: (payload: CreateAppRequest) => CreateAppAction = (payload) => {
    return {
        type: TRY_CREATE_APP,
        payload,
    };
};

export const tryAddApps: (payload: AddAppsRequest) => AddAppsAction = (payload) => {
    return {
        type: TRY_ADD_APPS,
        payload,
    };
};

// Delete app

export const tryDeleteApp: (request: DeleteAppRequest) => TryDeleteAppAction = (request) => {
    return {
        type: TRY_DELETE_APP_ENTITY,
        payload: request,
    };
};

export const appsDeleted: (payload: DeletedAppEntityPayload[]) => AppsDeletedAction = (payload) => {
    return {
        type: APPS_DELETED,
        payload,
    };
};

export const trySetAutomationPause: (
    requestData: SetAutomationPauseRequest,
    id?: string,
) => TrySetAutomationPauseAction = (requestData, id = null) => {
    return {
        type: TRY_SET_AUTOMATION_PAUSE,
        payload: {
            requestData,
            id: id ?? requestData.appId,
        },
    };
};

export const setAutomationPauseSuccess: (
    payload: SetAutomationPauseSuccessPayload,
) => SetAutomationPauseSuccessAction = (payload) => {
    return {
        type: SET_AUTOMATION_PAUSE_SUCCESS,
        payload,
    };
};
